import React, { useMemo, useState } from 'react';
import theme from './UnitStatusDisplay.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { Badge, Section } from '@ygtechnologies/rubus-ui';
import { toggleArrayValues } from '@/utils/arrays';
import { faHouse } from '@fortawesome/pro-solid-svg-icons';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { ResidentialUnitExtraData } from '@/types';
import { groupResidentialUnits } from '@/utils/residential-units';
import { t } from '@lingui/core/macro';
import { Link } from 'react-router-dom';

export type UnitStatusColor = 'hidden' | 'grey' | 'green' | 'yellow' | 'red';
const StatusColors: UnitStatusColor[] = ['green', 'yellow', 'red', 'grey'] as const;

interface UnitStatus {
    residentialUnitId: number;
    status: UnitStatusColor;
}

export interface UnitStatusDisplayProps {
    projectId: number;
    value: UnitStatus[];
    collapsible?: boolean;
    hideFilter?: boolean;
    onUnitClick?: (residentialUnitId: number) => void;
    onGroupClick?: (groupId: number | null) => void;
}

export const UnitStatusDisplay: React.FC<UnitStatusDisplayProps> = ({
    projectId,
    value,
    hideFilter = false,
    onUnitClick,
    onGroupClick,
}) => {
    const [selectedStatuses, setSelectedStatuses] = useState<UnitStatusColor[]>(StatusColors);
    const { data: units } = useQuery({
        queryKey: ['units', projectId],
        queryFn: () => getResidentialUnitsByProjectId(projectId),
    });

    const data = useMemo(() => {
        if (!units) return [];
        var map = new Map<number, UnitStatusColor>();

        value.forEach((x) => map.set(x.residentialUnitId, x.status));

        return units
            .map((u) => {
                const status = map.get(u.id) ?? 'hidden';
                return { unit: u, status };
            })
            .filter((x) => x.status !== 'hidden');
    }, [units, value]);

    const filteredData = useMemo(() => {
        const selectedStatusesSet = new Set(selectedStatuses);
        return data.filter((x) => selectedStatusesSet.has(x.status));
    }, [data, selectedStatuses]);

    const toggleStatus = (status: UnitStatusColor) => {
        setSelectedStatuses((oldValue) => toggleArrayValues(oldValue, [status], !oldValue.includes(status)));
    };

    var groupedData = groupResidentialUnits(filteredData, (x) => x.unit);
    const getUnitCount = (status) => data.filter((x) => x.status == status).length;

    return (
        <div>
            {!hideFilter && (
                <Section variant="highlight">
                    {StatusColors.map((status) => {
                        const count = getUnitCount(status);
                        if (!count) return null;
                        return (
                            <Badge
                                key={status}
                                size="lg"
                                className={classnames(theme.filter, {
                                    [theme.selected]: selectedStatuses.includes(status),
                                })}
                                onClick={(e) => toggleStatus(status)}>
                                <FontAwesomeIcon className={theme[status]} icon={faHouse} />
                                {getUnitCount(status)}/{data.length}
                            </Badge>
                        );
                    })}
                </Section>
            )}

            <div className={theme.groups}>
                {groupedData.map((x) => (
                    <UnitStatusDisplayGroup
                        key={x.id}
                        data={x.units}
                        title={x.name || t`Ungrouped`}
                        groupId={x.id}
                        onUnitClick={onUnitClick}
                        onGroupClick={onGroupClick}
                    />
                ))}
            </div>
        </div>
    );
};

interface UnitStatusDisplayGroupProps {
    title: string;
    groupId: number | null;
    data: {
        status: UnitStatusColor;
        unit: ResidentialUnitExtraData;
    }[];
    onUnitClick?: (residentialUnitId: number) => void;
    onGroupClick?: (groupId: number | null) => void;
}

const UnitStatusDisplayGroup: React.FC<UnitStatusDisplayGroupProps> = ({
    title,
    data,
    onUnitClick,
    onGroupClick,
    groupId,
}) => {
    return (
        <div className={theme.group}>
            <div className={theme.title} onClick={() => onGroupClick?.(groupId)}>
                {title}
            </div>
            <ul className={theme.list}>
                {data.map((x) => {
                    if (!x.status || x.status === 'hidden') return;
                    return (
                        <li key={x.unit.id} onClick={() => onUnitClick?.(x.unit.id)}>
                            <FontAwesomeIcon className={theme[x.status]} icon={faHouse} />
                            {x.unit.name}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
